import * as React from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Select } from "antd";
import axios, { isAxiosError } from "axios";
import { logger } from "@/helpers/tslog";
import { clearAuthLocalStorageAndReload, getToken } from "@/utils/localstorage";
import type { ContentSlidePrimaryCompany } from "@/state/contentSlideStore";

const DEBOUNCE_MS = 300;
const RESULT_LIMIT = 10;
export const PEERS_FILTER = "only-ten-tech";
const DEFAULT_EXCHANGE = "NASDAQ";

export interface CompanySearchResult {
  symbol: string;
  ticker: string;
  companyName: string;
  exchange: string;
  name?: string;
  webURL?: string;
  description?: string;
  ipoDate?: string | null;
}

type CategorizedSearchResults = Record<
  string,
  { metadata: { label: string; description: string }; tickers: CompanySearchResult[] }
>;

type SearchPeersResponse = CompanySearchResult[] | CategorizedSearchResults;

function extractDomainFromUrl(url?: string): string {
  if (!url) return "";
  try {
    const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
    return parsed.hostname.replace(/^www\./, "");
  } catch {
    return url
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .split("/")[0];
  }
}

function mapRawToCompany(row: Record<string, unknown>): CompanySearchResult {
  const symbolStr = String(row.symbol ?? "");
  const parts = symbolStr.split(":");
  return {
    symbol: symbolStr,
    ticker: String(row.ticker ?? parts[1] ?? ""),
    companyName: String(row.companyName ?? row.name ?? ""),
    exchange: String(row.exchange ?? parts[0] ?? ""),
    name: row.name != null ? String(row.name) : String(row.companyName ?? ""),
    webURL: row.webURL != null ? String(row.webURL) : "",
    description: row.description != null ? String(row.description) : "",
    ipoDate: (row.ipoDate as string | null | undefined) ?? null,
  };
}

function normalizePeersResponse(data: SearchPeersResponse): CompanySearchResult[] {
  const rows: Record<string, unknown>[] = Array.isArray(data)
    ? (data as unknown as Record<string, unknown>[])
    : Object.values(data).flatMap((cat) =>
        Array.isArray(cat.tickers) ? (cat.tickers as unknown as Record<string, unknown>[]) : []
      );

  const mapped = rows.map(mapRawToCompany);
  return Array.from(new Map(mapped.map((t) => [t.symbol, t])).values());
}

function toPrimaryCompany(row: CompanySearchResult): ContentSlidePrimaryCompany {
  const exchange = row.exchange || DEFAULT_EXCHANGE;
  const ticker = row.ticker || "";
  const symbol =
    row.symbol && row.symbol.includes(":") ? row.symbol : `${exchange}:${ticker}`;

  return {
    companyName: row.companyName || row.name || "",
    companyDomain: extractDomainFromUrl(row.webURL),
    companyTicker: ticker,
    companyExchange: exchange,
    symbol,
    companyColor: "",
    companyIpoDate: row.ipoDate ?? null,
  };
}

function optionLabel(c: CompanySearchResult): string {
  return `${c.companyName || c.name} (${c.ticker}) - ${c.exchange}`;
}

async function searchPeers(query: string): Promise<CompanySearchResult[]> {
  const apiUrl = process.env.API_URL as string;
  const token = await getToken();
  try {
    const { data } = await axios.get<SearchPeersResponse>(`${apiUrl}/search/peers`, {
      params: { search: query, filter: PEERS_FILTER },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
    return normalizePeersResponse(data).slice(0, RESULT_LIMIT);
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 401) {
      logger.warn("CompanySearchField: 401 from /search/peers — clearing session and reloading");
      clearAuthLocalStorageAndReload();
      return [];
    }
    throw e;
  }
}

export interface CompanySearchFieldProps {
  displayText: string;
  onSelectCompany: (company: ContentSlidePrimaryCompany) => void;
  className: string;
}

/** Pixels — trigger and Select use the same box so swapping does not jump layout */
const CONTROL_HEIGHT_PX = 40;

const TRIGGER_CLASS =
  "text-left font-normal text-sm text-gray-600 bg-white px-3 rounded-md cursor-pointer !border !border-gray-300 box-border hover:bg-gray-100 transition-colors flex min-w-0 w-full items-center justify-between gap-2";

/** Ant Design Select: flatten selector padding/height to match trigger */
const SELECT_ALIGN_CLASS =
  "[&_.ant-select-selector]:!min-h-0 [&_.ant-select-selector]:!h-10 [&_.ant-select-selector]:!py-0 [&_.ant-select-selector]:!px-3 [&_.ant-select-selector]:rounded-md [&_.ant-select-selector]:flex [&_.ant-select-selector]:items-center " +
  "[&_.ant-select-selection-wrap]:!self-center [&_.ant-select-selection-search]:!m-0 [&_.ant-select-selection-search]:flex [&_.ant-select-selection-search]:items-center " +
  "[&_.ant-select-selection-search-input]:!h-8 [&_.ant-select-selection-search-input]:leading-8 [&_.ant-select-selection-placeholder]:leading-8";

export function CompanySearchField({ displayText, onSelectCompany, className }: CompanySearchFieldProps) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<CompanySearchResult[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        setSearchResults(await searchPeers(q));
      } catch (e) {
        logger.error("CompanySearchField: search failed", e);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const onSelect = (symbol: string) => {
    const row = searchResults.find((c) => c.symbol === symbol);
    if (!row) {
      setOpen(false);
      setSearchQuery("");
      return;
    }
    const payload = toPrimaryCompany(row);
    onSelectCompany(payload);
    logger.info("CompanySearchField: selected", payload.symbol);
    setOpen(false);
    setSearchQuery("");
  };

  const selectOptions = searchResults.map((c) => ({ label: optionLabel(c), value: c.symbol }));

  return (
    <div className={`${className}`}>
      {!open ? (
        <div
          role="button"
          tabIndex={0}
          className={TRIGGER_CLASS}
          style={{ height: CONTROL_HEIGHT_PX, minHeight: CONTROL_HEIGHT_PX }}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setOpen(true);
          }}
        >
          <span className="min-w-0 flex-1 truncate">{displayText}</span>
          <SearchOutlined className="shrink-0 text-base text-gray-500" aria-hidden />
        </div>
      ) : (
        <Select
          showSearch
          autoFocus
          open={open}
          loading={isLoading}
          options={selectOptions}
          filterOption={false}
          className={`w-full rounded-md ${SELECT_ALIGN_CLASS}`}
          styles={{
            root: {
              minHeight: CONTROL_HEIGHT_PX,
              height: CONTROL_HEIGHT_PX,
              textAlign: "left",
              paddingTop: 0
            },
          }}
          placeholder="Search for a company..."
          notFoundContent={
            isLoading ? "Searching..." : searchQuery.trim() ? "No companies found" : "Type to search"
          }
          onSearch={setSearchQuery}
          onChange={onSelect}
          onDropdownVisibleChange={setOpen}
        />
      )}
    </div>
  );
}
